import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBuilding, FaClock, FaEnvelope, FaFolderOpen, FaPen, FaPhone, FaTimes, FaUser, FaWhatsapp } from 'react-icons/fa';
import './ContactCard.css';

export const weekDays = [
  ['lunedi', 'Lunedì'], ['martedi', 'Martedì'], ['mercoledi', 'Mercoledì'], ['giovedi', 'Giovedì'],
  ['venerdi', 'Venerdì'], ['sabato', 'Sabato'], ['domenica', 'Domenica']
];

export const emptyReceptionSchedule = () => Object.fromEntries(weekDays.map(([key]) => [key, {
  attivo: false, mattinaDa: '', mattinaA: '', pomeriggioDa: '', pomeriggioA: ''
}]));

export const normalizeReceptionSchedule = value => {
  const base = emptyReceptionSchedule();
  if (!value || typeof value !== 'object') return base;
  weekDays.forEach(([key]) => { base[key] = { ...base[key], ...(value[key] || {}) }; });
  return base;
};

export const whatsappUrl = phone => {
  const original = String(phone || '').trim();
  let digits = original.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  else if (!original.startsWith('+') && !digits.startsWith('39')) digits = `39${digits}`;
  return `https://wa.me/${digits}`;
};

const rangesForDay = day => [
  day?.mattinaDa && day?.mattinaA ? `${day.mattinaDa}–${day.mattinaA}` : '',
  day?.pomeriggioDa && day?.pomeriggioA ? `${day.pomeriggioDa}–${day.pomeriggioA}` : ''
].filter(Boolean);
const isWithinRange = (value, start, end) => start && end && value >= start && value <= end;

export function ReceptionHours({ schedule, compact = false }) {
  const [open, setOpen] = useState(false);
  const normalized = normalizeReceptionSchedule(schedule);
  const jsDay = new Date().getDay();
  const todayIndex = jsDay === 0 ? 6 : jsDay - 1;
  const [todayKey, todayLabel] = weekDays[todayIndex];
  const today = normalized[todayKey];
  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const todayRanges = today?.attivo ? rangesForDay(today) : [];
  const callable = today?.attivo && (isWithinRange(currentTime, today.mattinaDa, today.mattinaA) || isWithinRange(currentTime, today.pomeriggioDa, today.pomeriggioA));
  const hasSchedule = weekDays.some(([key]) => normalized[key]?.attivo && rangesForDay(normalized[key]).length);
  if (!hasSchedule) return null;
  return <>
    <div className={`reception-quick${compact ? ' compact' : ''}`}>
      <FaClock /><span><small>{todayLabel}</small><strong>{todayRanges.length ? todayRanges.join(' · ') : 'Nessun ricevimento'}</strong></span>
      <em className={callable ? 'available' : ''}>{callable ? 'Chiamabile ora' : 'Fuori orario'}</em>
      <button type="button" onClick={() => setOpen(true)}>Vedi tutti gli orari</button>
    </div>
    {open && <div className="reception-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && setOpen(false)}>
      <section className="reception-dialog" role="dialog" aria-modal="true" aria-label="Orari di ricevimento">
        <header><div><small>LIQUIDATORE</small><h3>Orari di ricevimento</h3></div><button type="button" aria-label="Chiudi" onClick={() => setOpen(false)}><FaTimes /></button></header>
        <div>{weekDays.map(([key, label], index) => {
          const ranges = normalized[key]?.attivo ? rangesForDay(normalized[key]) : [];
          return <p className={index === todayIndex ? 'today' : ''} key={key}><strong>{label}</strong><span>{ranges.length ? ranges.join(' · ') : '—'}</span></p>;
        })}</div>
      </section>
    </div>}
  </>;
}

export default function ContactCard({ contact = {}, role, name, detailTo, onOpen, onEdit, compact = false, showPracticeCount = false, actionOnly = false, emailSubject = '' }) {
  const emailHref = address => `mailto:${address}${emailSubject ? '?subject='+encodeURIComponent(emailSubject) : ''}`;
  const displayName = name || contact.nome || 'Da indicare';
  const emails = (contact.emails || (contact.email ? [{ tipo: contact.emailTipo || 'Email', email: contact.email }] : []))
    .map(item => ({ ...item, email: item.email || item.valore || '' })).filter(item => item.email);
  const phones = (contact.telefoni || (contact.telefono ? [{ tipo: 'Cellulare', telefono: contact.telefono, whatsapp: Boolean(contact.telefono_whatsapp) }] : []))
    .map(item => ({ ...item, telefono: item.telefono || item.valore || '' })).filter(item => item.telefono);
  const whatsappPhones = phones.filter(item => Boolean(item.whatsapp) || String(item.tipo || '').toLocaleLowerCase('it-IT') === 'whatsapp');
  const whatsapp = phones.find(item => Boolean(item.whatsapp) || String(item.tipo || '').toLocaleLowerCase('it-IT') === 'whatsapp');
  const phone = phones[0];
  const email = emails.find(item => String(item.tipo || '').toUpperCase() === 'PEC') || emails[0];
  const hasQuickAction = Boolean(emails.length || phones.length || whatsappPhones.length);
  const Heading = detailTo ? Link : 'button';
  const headingProps = detailTo ? { to: detailTo } : { type: 'button', onClick: onOpen };
  const quickAction = (kind, items, icon, label, hrefFor, extraClass = '') => {
    if (!items.length) return null;
    if (items.length === 1) return <a className={`contact-quick-action ${extraClass}`.trim()} href={hrefFor(items[0])}
      target={kind === 'whatsapp' ? '_blank' : undefined} rel={kind === 'whatsapp' ? 'noreferrer' : undefined}
      aria-label={`${label}: ${items[0].email || items[0].telefono}`}>{icon}<span>{label}</span><small>{items[0].email || items[0].telefono}</small></a>;
    return <label className={`contact-quick-action ${extraClass}`.trim()} aria-label={`Scegli ${label.toLocaleLowerCase('it-IT')}`}>
      {icon}<span>{label}</span><small>{items[0].email || items[0].telefono}</small><em>+{items.length - 1}</em>
      <select defaultValue="" aria-label={`Scegli ${label.toLocaleLowerCase('it-IT')}`} onChange={event => {
        if (event.target.value === '') return;
        const selectedItem = items[Number(event.target.value)];
        const href = hrefFor(selectedItem);
        if (kind === 'whatsapp') window.open(href, '_blank', 'noopener,noreferrer');
        else window.location.href = href;
        event.target.value = '';
      }}>
        <option value="" disabled>Scegli il recapito</option>
        {items.map((item, index) => <option key={`${item.email || item.telefono}-${index}`} value={index}>{item.tipo || label} · {item.email || item.telefono}</option>)}
      </select>
    </label>;
  };

  if (compact) return <article className="shared-contact-card compact quick-contact-card">
    <Heading className="shared-contact-heading" {...headingProps}><span>{role || contact.tipo || 'Contatto'}</span><strong>{displayName}</strong></Heading>
    <div className="contact-quick-actions" aria-label={`Azioni rapide ${role || 'contatto'}`}>
      {quickAction('phone', phones, <FaPhone />, 'Chiama', item => `tel:${String(item.telefono).replace(/[^+0-9]/g, '')}`)}
      {quickAction('email', emails, <FaEnvelope />, 'Scrivi', item => emailHref(item.email))}
      {quickAction('whatsapp', whatsappPhones, <FaWhatsapp />, 'WhatsApp', item => whatsappUrl(item.telefono), 'whatsapp')}
      {actionOnly && !hasQuickAction && detailTo && <Link className="contact-quick-action empty-action" to={detailTo}><FaPen /><span>Aggiungi recapiti</span></Link>}
    </div>
    {(hasQuickAction || contact.indirizzo || contact.iban || detailTo || onOpen) && <details className="contact-inline-details">
      <summary>Mostra riferimenti</summary>
      <div className="contact-picker-details">
          {!!phones.length && <div><small>Telefoni</small>{phones.map((item, index) => <p key={`${item.telefono}-${index}`}><span>{item.tipo || 'Telefono'}</span><strong>{item.telefono}</strong>{item.whatsapp && <FaWhatsapp />}</p>)}</div>}
          {!!emails.length && <div><small>Email</small>{emails.map((item, index) => <p key={`${item.email}-${index}`}><span>{item.tipo || 'Email'}</span><strong>{item.email}</strong></p>)}</div>}
          {contact.indirizzo && <div><small>Indirizzo</small><p><strong>{contact.indirizzo}</strong></p></div>}
          {contact.iban && <div><small>IBAN</small><p><strong>{contact.iban}</strong></p></div>}
          {!hasQuickAction && !contact.indirizzo && !contact.iban && <p className="contact-picker-empty">Nessun riferimento inserito.</p>}
          {detailTo && <Link className="contact-picker-edit" to={detailTo}><FaPen /> Apri scheda completa</Link>}
          {onOpen && <button type="button" className="contact-picker-edit" onClick={onOpen}><FaPen /> Apri scheda completa</button>}
      </div>
    </details>}
  </article>;

  return <article className={`shared-contact-card${compact ? ' compact' : ''}`}>
    {!compact && <div className="shared-contact-icon">{contact.tipo_soggetto === 'azienda' ? <FaBuilding /> : <FaUser />}</div>}
    <Heading className="shared-contact-heading" {...headingProps}><span>{role || contact.tipo || 'Contatto'}</span><strong>{displayName}</strong></Heading>
    {contact.tipo === 'Centro fisioterapico' && contact.convenzionato && <div className="shared-contact-agreement">Convenzionato CISA</div>}
    {!!contact.compagnie_collegate?.length && <div className="shared-contact-relations"><span>Compagnie collegate</span><strong>{contact.compagnie_collegate.map(item => item.nome).join(' · ')}</strong></div>}
    <div className="shared-contact-links">
      {email ? <a href={emailHref(email.email)}><FaEnvelope /><span>{email.tipo || 'Email'}<strong>{email.email}</strong></span></a> : !actionOnly && <span className="empty"><FaEnvelope /> Email da indicare</span>}
      {phone ? <a href={`tel:${String(phone.telefono).replace(/[^+0-9]/g, '')}`}><FaPhone /><span>{phone.tipo || 'Telefono'}<strong>{phone.telefono}</strong></span></a> : !actionOnly && <span className="empty"><FaPhone /> Telefono da indicare</span>}
      {whatsapp && <a className="whatsapp" href={whatsappUrl(whatsapp.telefono)} target="_blank" rel="noreferrer"><FaWhatsapp /><span>WhatsApp<strong>{whatsapp.telefono}</strong></span></a>}
      {actionOnly && !hasQuickAction && detailTo && <Link className="shared-contact-empty-action" to={detailTo}><FaPen /><span>Aggiungi recapito<strong>Apri Dati cliente</strong></span></Link>}
    </div>
    {contact.tipo === 'Liquidatore' && <ReceptionHours schedule={contact.orari_ricevimento} compact={compact} />}
    {(showPracticeCount || onEdit) && <footer>{showPracticeCount && <span><FaFolderOpen /> {contact.numero_pratiche || 0} pratiche</span>}{onEdit && <button type="button" onClick={onEdit}><FaPen /> Modifica</button>}</footer>}
  </article>;
}
